/**
 * IndexedDB wrapper for offline data storage.
 *
 * Stores:
 * - audio-recordings: raw audio blobs saved offline
 * - trip-report-drafts: queued trip report saves
 * - cached-accounts: offline account list cache
 */

const DB_NAME = "copilot-offline";
const DB_VERSION = 1;

const STORES = {
  AUDIO_RECORDINGS: "audio-recordings",
  TRIP_REPORT_DRAFTS: "trip-report-drafts",
  CACHED_ACCOUNTS: "cached-accounts",
} as const;

export type StoreName = (typeof STORES)[keyof typeof STORES];

export interface QueuedAudioUpload {
  id: string;
  blob: Blob;
  mimeType: string;
  duration: number;
  createdAt: number;
  retryCount: number;
}

export interface QueuedTripReport {
  id: string;
  payload: unknown;
  createdAt: number;
  retryCount: number;
}

export interface CachedAccount {
  id: string;
  data: unknown;
  cachedAt: number;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORES.AUDIO_RECORDINGS)) {
        db.createObjectStore(STORES.AUDIO_RECORDINGS, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORES.TRIP_REPORT_DRAFTS)) {
        db.createObjectStore(STORES.TRIP_REPORT_DRAFTS, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORES.CACHED_ACCOUNTS)) {
        db.createObjectStore(STORES.CACHED_ACCOUNTS, { keyPath: "id" });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function getStore(
  name: StoreName,
  mode: IDBTransactionMode = "readonly",
): Promise<IDBObjectStore> {
  const db = await openDB();
  return db.transaction(name, mode).objectStore(name);
}

function idbRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ─── Audio recording storage ────────────────────────────────────────

export async function saveAudioRecording(
  blob: Blob,
  mimeType: string,
  duration: number,
): Promise<string> {
  const id = `audio_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const store = await getStore(STORES.AUDIO_RECORDINGS, "readwrite");
  const record: QueuedAudioUpload = {
    id,
    blob,
    mimeType,
    duration,
    createdAt: Date.now(),
    retryCount: 0,
  };
  await idbRequest(store.put(record));
  return id;
}

export async function getAudioRecording(
  id: string,
): Promise<QueuedAudioUpload | undefined> {
  const store = await getStore(STORES.AUDIO_RECORDINGS);
  return idbRequest(store.get(id));
}

export async function getAllAudioRecordings(): Promise<QueuedAudioUpload[]> {
  const store = await getStore(STORES.AUDIO_RECORDINGS);
  return idbRequest(store.getAll());
}

export async function deleteAudioRecording(id: string): Promise<void> {
  const store = await getStore(STORES.AUDIO_RECORDINGS, "readwrite");
  await idbRequest(store.delete(id));
}

// ─── Trip report draft storage ──────────────────────────────────────

export async function saveTripReportDraft(
  payload: unknown,
): Promise<string> {
  const id = `trip_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const store = await getStore(STORES.TRIP_REPORT_DRAFTS, "readwrite");
  const record: QueuedTripReport = {
    id,
    payload,
    createdAt: Date.now(),
    retryCount: 0,
  };
  await idbRequest(store.put(record));
  return id;
}

export async function getAllTripReportDrafts(): Promise<QueuedTripReport[]> {
  const store = await getStore(STORES.TRIP_REPORT_DRAFTS);
  return idbRequest(store.getAll());
}

export async function deleteTripReportDraft(id: string): Promise<void> {
  const store = await getStore(STORES.TRIP_REPORT_DRAFTS, "readwrite");
  await idbRequest(store.delete(id));
}

// ─── Cached accounts ────────────────────────────────────────────────

export async function cacheAccounts(accounts: unknown[]): Promise<void> {
  const store = await getStore(STORES.CACHED_ACCOUNTS, "readwrite");
  const record: CachedAccount = {
    id: "account-list",
    data: accounts,
    cachedAt: Date.now(),
  };
  await idbRequest(store.put(record));
}

export async function getCachedAccounts(): Promise<CachedAccount | undefined> {
  const store = await getStore(STORES.CACHED_ACCOUNTS);
  return idbRequest(store.get("account-list"));
}

export async function cacheQuickFacts(
  accountId: string,
  data: unknown,
): Promise<void> {
  const store = await getStore(STORES.CACHED_ACCOUNTS, "readwrite");
  const record: CachedAccount = {
    id: `quick-facts-${accountId}`,
    data,
    cachedAt: Date.now(),
  };
  await idbRequest(store.put(record));
}

export async function getCachedQuickFacts(
  accountId: string,
): Promise<CachedAccount | undefined> {
  const store = await getStore(STORES.CACHED_ACCOUNTS);
  return idbRequest(store.get(`quick-facts-${accountId}`));
}

export { STORES };
